import { getFirestore, collection, onSnapshot, doc, updateDoc, query, orderBy, limit } from 'firebase/firestore';
import reminderBackendApiService from './reminderBackendApiService';
// Import the existing Firebase auth from your setup
import { auth } from '../main-component/Firebase';

class NotificationService {
  constructor() {
    this.notifications = [];
    this.unreadCount = 0;
    this.listeners = [];
    this.firestoreUnsubscribe = null;
    this.db = null;
    this.isInitialized = false;
    this.isAuthenticated = false;
    this.initializeService();
  }

  // Initialize Firestore connection with authentication check
  async initializeService() {
    try {
      this.db = getFirestore();
      
      // Check authentication state before setting up Firestore listener using compat auth
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          this.isAuthenticated = true;
          this.setupFirestoreListener();
        } else {
          try {
            // Sign in anonymously for notification access using compat auth
            await auth.signInAnonymously();
            this.isAuthenticated = true;
            this.setupFirestoreListener();
          } catch (error) {
            console.log('Anonymous authentication failed, using local storage and backend API only:', error);
            this.isAuthenticated = false;
            this.loadFromStorage();
          }
        }
      });
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing notification service:', error);
      // Fallback to localStorage if Firestore fails
      this.loadFromStorage();
    }
  }

  // Setup real-time listener for Firestore notifications
  setupFirestoreListener() {
    if (!this.db || !this.isAuthenticated) {
      console.log('Firestore not available or user not authenticated, skipping listener setup');
      return;
    }

    try {
      const notificationsRef = collection(this.db, 'notifications');
      const notificationsQuery = query(
        notificationsRef, 
        orderBy('timestamp', 'desc'), 
        limit(100)
      );

      this.firestoreUnsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
        const notifications = [];
        let unreadCount = 0;

        snapshot.forEach((doc) => {
          const notification = { id: doc.id, ...doc.data() };
          notifications.push(notification);
          if (!notification.read) {
            unreadCount++;
          }
        });

        this.notifications = notifications;
        this.unreadCount = unreadCount;
        this.saveToStorage(); // Keep localStorage as backup
        this.notifyListeners();
      }, (error) => {
        console.error('Firestore listener error:', error);
        // Fallback to localStorage
        this.loadFromStorage();
      });
    } catch (error) {
      console.error('Error setting up Firestore listener:', error);
      this.loadFromStorage();
    }
  }

  // Load notifications from localStorage (fallback)
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('mostre-notifications');
      if (stored) {
        const data = JSON.parse(stored);
        this.notifications = data.notifications || [];
        this.unreadCount = data.unreadCount || 0;
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Error loading notifications from storage:', error);
    }
  }

  // Save notifications to localStorage (backup)
  saveToStorage() {
    try {
      localStorage.setItem('mostre-notifications', JSON.stringify({
        notifications: this.notifications,
        unreadCount: this.unreadCount
      }));
    } catch (error) {
      console.error('Error saving notifications to storage:', error);
    }
  }

  // Add a new notification (now only used for local notifications, Firestore notifications come from backend)
  addNotification(notification) {
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };

    this.notifications.unshift(newNotification);
    this.unreadCount++;
    this.saveToStorage();
    this.notifyListeners();
    return newNotification.id;
  }

  // Mark notification as read (update both Firestore and backend API)
  async markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      // Update local state immediately
      notification.read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      this.saveToStorage();
      this.notifyListeners();

      // Update Firestore if available and authenticated
      if (this.db && this.isInitialized && this.isAuthenticated) {
        try {
          const notificationRef = doc(this.db, 'notifications', notificationId);
          await updateDoc(notificationRef, { read: true });
        } catch (error) {
          console.error('Error updating notification in Firestore:', error);
        }
      }

      // Update backend API
      try {
        await reminderBackendApiService.markNotificationAsRead(notificationId);
      } catch (error) {
        console.error('Error updating notification in backend API:', error);
      }
    }
  }

  // Mark notification as unread (update both Firestore and backend API)
  async markAsUnread(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification && notification.read) {
      // Update local state immediately
      notification.read = false;
      this.unreadCount++;
      this.saveToStorage();
      this.notifyListeners();

      // Update Firestore if available and authenticated
      if (this.db && this.isInitialized && this.isAuthenticated) {
        try {
          const notificationRef = doc(this.db, 'notifications', notificationId);
          await updateDoc(notificationRef, { read: false });
        } catch (error) {
          console.error('Error updating notification in Firestore:', error);
        }
      }

      // Update backend API
      try {
        await reminderBackendApiService.markNotificationAsUnread(notificationId);
      } catch (error) {
        console.error('Error updating notification in backend API:', error);
      }
    }
  }

  // Update unread count based on current notifications
  updateUnreadCount = () => {
    this.unreadCount = this.notifications.filter(n => !n.read).length;
  }

  // Get all notifications
  getNotifications() {
    return this.notifications;
  }

  // Get unread count
  getUnreadCount() {
    return this.unreadCount;
  }

  // Delete a single notification
  async removeNotification(notificationId) {
    try {
      // Remove from backend first
      if (this.isAuthenticated) {
        await reminderBackendApiService.deleteNotification(notificationId);
      }
    } catch (error) {
      console.error('Error deleting notification from backend:', error);
      // Continue with local deletion even if backend fails
    }
    
    // Remove from local state
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.updateUnreadCount();
    this.saveToStorage();
    this.notifyListeners();
  }

  // Delete all notifications
  async clearAll() {
    try {
      // Delete from backend first
      if (this.isAuthenticated) {
        await reminderBackendApiService.deleteAllNotifications();
      }
    } catch (error) {
      console.error('Error deleting all notifications from backend:', error);
      // Continue with local deletion even if backend fails
    }
    
    // Clear local state
    this.notifications = [];
    this.unreadCount = 0;
    this.saveToStorage();
    this.notifyListeners();
  }

  // Cleanup Firestore listener
  cleanup() {
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }
  }

  // Check if service is connected to Firestore
  isConnectedToFirestore() {
    return this.isInitialized && this.db !== null && this.isAuthenticated;
  }

  // Subscribe to changes
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener({
          notifications: this.notifications,
          unreadCount: this.unreadCount
        });
      } catch (error) {
        console.error('Error notifying listener:', error);
      }
    });
  }

  // Create a follow-up notification
  async createFollowUpNotification(name, lastname, phone) {
    try {
      // Send notification to backend API first
      await reminderBackendApiService.sendFollowUpNotification(name, lastname, phone);
      console.log(`Backend notification sent for ${name} ${lastname}`);
    } catch (error) {
      console.error('Error sending notification to backend:', error);
      // Continue with local notification even if backend fails
    }
    
    // Create local notification for immediate UI feedback
    const notification = {
      type: 'followup',
      title: 'Follow-up Reminder',
      message: `Vezi ce mai face ${name} ${lastname}`,
      name: String(name || ''),
      lastname: String(lastname || ''),
      phone: String(phone || ''),
      category: 'reminder'
    };
    
    return this.addNotification(notification);
  }

  // Sync pending notifications with backend (retry mechanism)
  async syncWithBackend() {
    if (!this.isInitialized) return;
    
    // This could be called periodically to retry failed backend operations
    const pendingNotifications = this.notifications.filter(n => n.pendingSync);
    
    for (const notification of pendingNotifications) {
      try {
        if (notification.name && notification.lastname && notification.phone) {
          await reminderBackendApiService.sendFollowUpNotification(
            notification.name,
            notification.lastname,
            notification.phone
          );
          
          // Mark as synced
          notification.pendingSync = false;
          this.saveToStorage();
        }
      } catch (error) {
        console.error('Error syncing notification with backend:', error);
      }
    }
  }

  /**
   * Send rank request notification to admin
   * @param {Object} user - User requesting rank upgrade
   * @param {string} currentRank - Current user rank
   * @param {string} requestedRank - Requested new rank
   * @returns {Promise} - Notification result
   */
  async sendRankRequestToAdmin(user, currentRank, requestedRank) {
    try {
      const notification = {
        id: Date.now().toString(),
        type: 'rank_request',
        title: 'Cerere de promovare',
        message: `${user.firstName} ${user.lastName} a aplicat pentru noul rank de ${requestedRank}`,
        user: user,
        currentRank: currentRank,
        requestedRank: requestedRank,
        timestamp: new Date().toISOString(),
        status: 'pending',
        priority: 'medium',
        isRead: false,
        targetUserIds: await this.getAdminUserIds()
      };

      // Add to notifications list
      await this.addNotification(notification);

      // Send email notification to admins if available
      try {
        const { default: userService } = await import('./userService');
        const admins = await this.getAdminUsers();
        if (admins.length > 0) {
          await userService.sendBulkEmail(
            admins,
            'Nouă cerere de promovare',
            {
              requestMessage: `${user.firstName} ${user.lastName} a solicitat promovarea de la ${currentRank} la ${requestedRank}`,
              userFirstName: user.firstName,
              userLastName: user.lastName,
              userEmail: user.email,
              currentRank: currentRank,
              requestedRank: requestedRank,
              requestDate: new Date().toLocaleDateString('ro-RO')
            },
            'rank_request'
          );
        }
      } catch (emailError) {
        console.warn('Could not send email notification to admins:', emailError);
        // Continue even if email fails
      }

      console.log('Rank request notification sent successfully:', notification);
      return notification;
    } catch (error) {
      console.error('Error sending rank request notification:', error);
      throw error;
    }
  }

  /**
   * Send rank approval notification to user
   * @param {Object} user - User who requested rank
   * @param {string} newRank - Approved rank
   * @returns {Object} - Notification result
   */
  async sendRankApprovalNotification(user, newRank) {
    try {
      const notification = {
        id: Date.now().toString(),
        type: 'rank_approval',
        title: 'Cererea de promovare a fost aprobată!',
        message: `Felicitări! Cererea ta de promovare la ${newRank} a fost aprobată.`,
        timestamp: new Date().toISOString(),
        priority: 'high',
        isRead: false,
        targetUserIds: [user.id],
        userId: user.id
      };

      await this.addNotification(notification);

      // Update corresponding admin notification status
      const adminNotification = this.notifications.find(n => 
        n.type === 'rank_request' && 
        n.user.id === user.id && 
        n.status === 'pending'
      );
      
      if (adminNotification) {
        adminNotification.status = 'approved';
        adminNotification.resolvedAt = new Date().toISOString();
        this.saveToStorage();
      }

      return notification;
    } catch (error) {
      console.error('Error sending rank approval notification:', error);
      throw error;
    }
  }

  /**
   * Send rank rejection notification to user
   * @param {Object} user - User who requested rank
   * @param {string} reason - Rejection reason
   * @returns {Object} - Notification result
   */
  async sendRankRejectionNotification(user, reason = '') {
    try {
      const message = reason 
        ? `Cererea ta de promovare a fost respinsă. Motiv: ${reason}`
        : 'Cererea ta de promovare a fost respinsă.';

      const notification = {
        id: Date.now().toString(),
        type: 'rank_rejection',
        title: 'Cererea de promovare a fost respinsă',
        message: message,
        timestamp: new Date().toISOString(),
        priority: 'medium',
        isRead: false,
        targetUserIds: [user.id],
        userId: user.id,
        reason: reason
      };

      await this.addNotification(notification);

      // Update corresponding admin notification status
      const adminNotification = this.notifications.find(n => 
        n.type === 'rank_request' && 
        n.user.id === user.id && 
        n.status === 'pending'
      );
      
      if (adminNotification) {
        adminNotification.status = 'rejected';
        adminNotification.reason = reason;
        adminNotification.resolvedAt = new Date().toISOString();
        this.saveToStorage();
      }

      return notification;
    } catch (error) {
      console.error('Error sending rank rejection notification:', error);
      throw error;
    }
  }

  /**
   * Get admin users for notifications
   * @returns {Promise<Array>} - Array of admin users
   */
  async getAdminUsers() {
    try {
      const { default: userService } = await import('./userService');
      const users = await userService.getUsers();
      const usersArray = Array.isArray(users) ? users : [users];
      return usersArray.filter(user => 
        user.role === 'admin' || 
        user.isAdmin === true ||
        user.rank === 'admin' ||
        user.rank === 'super_admin'
      );
    } catch (error) {
      console.error('Error getting admin users:', error);
      return [];
    }
  }

  /**
   * Get admin user IDs for targeting notifications
   * @returns {Promise<Array>} - Array of admin user IDs
   */
  async getAdminUserIds() {
    try {
      const admins = await this.getAdminUsers();
      return admins.map(admin => admin.id);
    } catch (error) {
      console.error('Error getting admin user IDs:', error);
      return [];
    }
  }

  /**
   * Get notifications for specific user
   * @param {string} userId - User ID
   * @returns {Array} - Array of user notifications
   */
  getNotificationsForUser(userId) {
    return this.notifications.filter(notification => 
      notification.targetUserIds?.includes(userId) || 
      notification.userId === userId
    );
  }

  /**
   * Get pending rank requests
   * @returns {Array} - Array of pending rank request notifications
   */
  getPendingRankRequests() {
    return this.notifications.filter(notification => 
      notification.type === 'rank_request' && 
      notification.status === 'pending'
    );
  }
}

// Create and export singleton instance
const notificationService = new NotificationService();
export default notificationService;
